import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ItemSimulacaoBoletoUnico,
  NegociacaoBoletoUnico,
  TituloBoletoUnico,
  SimulacaoBoletoUnico,
  encargosTotais,
  hasBoleto,
  hasPix,
} from "src/@types/boletoUnico";
import BoletoUnicoModal from "src/components/boleto-unico/BoletoUnicoModal";
import {
  buscarParcelasEmAtraso,
  consultar,
  gerar,
  parcelaParaTitulo,
  simular,
} from "src/components/boleto-unico/boletoUnicoApi";
import EmptyContent from "src/components/EmptyContent";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import useAuth from "src/hooks/useAuth";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";

const moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const valor = (v: number) => moeda.format(v);
const dataCurta = (d?: Date | null) => (d ? moment(d).format("DD/MM/YYYY") : "--");
const mesAno = (d: Date) => moment(d).format("MMM/YYYY").toUpperCase().replace(/\./g, "");

const erroToast = (mensagem: string) =>
  toast.error(mensagem, { position: toast.POSITION.TOP_RIGHT, theme: "colored" });

type Estado =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "error"; message: string }
  | { kind: "pendente"; negociacao: NegociacaoBoletoUnico }
  | { kind: "simulacao"; simulacao: SimulacaoBoletoUnico };

export default function BoletoUnico() {
  const { themeStretch } = useSettings();
  const { user, selectedContract } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const contractNumber = selectedContract?.contract_number ?? "";

  const [estado, setEstado] = useState<Estado>({ kind: "loading" });
  const [titulos, setTitulos] = useState<TituloBoletoUnico[]>([]);
  const [gerando, setGerando] = useState(false);
  const [negociacaoModal, setNegociacaoModal] = useState<NegociacaoBoletoUnico | null>(null);

  const voltar = () => navigate(PATH_DASHBOARD.general.historic);

  const carregar = useCallback(async () => {
    setEstado({ kind: "loading" });
    setTitulos([]);

    // Falha ao consultar não derruba a tela: segue para a simulação.
    let negociacoes: NegociacaoBoletoUnico[] = [];
    try {
      negociacoes = await consultar(contractNumber);
    } catch (error: any) {
      erroToast(error.message);
    }

    // Enquanto houver negociação em aberto o Protheus não aceita uma nova.
    const pendente = negociacoes.find((n) => !n.pagamentoConfirmado);
    if (pendente) {
      setEstado({ kind: "pendente", negociacao: pendente });
      return;
    }

    try {
      const parcelas = await buscarParcelasEmAtraso(user.id, contractNumber);
      if (parcelas.length === 0) {
        setEstado({ kind: "empty" });
        return;
      }

      const novosTitulos = parcelas.map(parcelaParaTitulo);
      setTitulos(novosTitulos);

      const simulacao = await simular({ contract_number: contractNumber, titulos: novosTitulos });
      setEstado({ kind: "simulacao", simulacao });
    } catch (error: any) {
      setEstado({ kind: "error", message: error.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractNumber, user?.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const gerarBoleto = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const negociacao = await gerar({ contract_number: contractNumber, titulos });
      if (hasBoleto(negociacao) || hasPix(negociacao)) {
        setNegociacaoModal(negociacao);
      } else {
        // O Protheus registra o boleto no banco depois de gerar a negociação.
        toast.success("Boleto em processamento. Consulte em instantes.", {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        });
        voltar();
      }
    } catch (error: any) {
      erroToast(error.message);
    }
    setGerando(false);
  };

  const fecharModal = () => {
    setNegociacaoModal(null);
    voltar();
  };

  return (
    <Page title="Boleto Único">
      <Container maxWidth={themeStretch ? false : "lg"}>
        <HeaderBreadcrumbs
          arrowClickBack={voltar}
          heading="Regularizar contrato"
          links={[
            { name: "Início", href: PATH_DASHBOARD.root },
            { name: "Histórico Financeiro", href: PATH_DASHBOARD.general.historic },
            { name: "Regularizar contrato" },
          ]}
        />
        <Divider sx={{ marginBottom: "20px" }} />

        {estado.kind === "loading" && (
          <Grade
            principal={<Skeleton variant="rounded" height={260} />}
            resumo={<Skeleton variant="rounded" height={180} />}
          />
        )}

        {estado.kind === "empty" && (
          <Card>
            <EmptyContent title="Não há parcelas em atraso neste contrato." />
          </Card>
        )}

        {estado.kind === "error" && (
          <Card>
            <EmptyContent
              title="Não foi possível simular o boleto único."
              description={estado.message}
            />
            <Box sx={{ display: "flex", justifyContent: "center", pb: 4 }}>
              <Button variant="contained" color="secondary" onClick={carregar}>
                Tentar novamente
              </Button>
            </Box>
          </Card>
        )}

        {estado.kind === "simulacao" && (
          <Grade
            principal={
              <Card sx={{ p: 3 }}>
                <Typography variant="subtitle1">Parcelas incluídas</Typography>
                <Typography variant="body2" color="text.secondary">
                  Todas as parcelas em atraso do contrato
                </Typography>
                {estado.simulacao.itens.map((item, index) => (
                  <Box key={`${item.item}-${index}`}>
                    <Divider sx={{ my: 2 }} />
                    <LinhaParcela item={item} />
                  </Box>
                ))}
              </Card>
            }
            resumo={
              <Card sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Juros e encargos
                  </Typography>
                  <Typography variant="body2">{valor(encargosTotais(estado.simulacao))}</Typography>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1">Valor do boleto</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vencimento em {dataCurta(estado.simulacao.vencimentoSugerido)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color={theme.palette.secondary.main} fontWeight={700}>
                    {valor(estado.simulacao.valorNegociado)}
                  </Typography>
                </Stack>

                <Stack spacing={1} sx={{ mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    loading={gerando}
                    onClick={gerarBoleto}
                  >
                    Gerar boleto
                  </LoadingButton>
                  <Button color="inherit" fullWidth onClick={voltar}>
                    Cancelar
                  </Button>
                </Stack>
              </Card>
            }
          />
        )}

        {estado.kind === "pendente" && (
          <Grade
            principal={
              <Card sx={{ p: 3 }}>
                <Typography variant="subtitle1">Negociação em aberto</Typography>
                <Typography variant="body2" color="text.secondary">
                  Boleto {estado.negociacao.codigo} ·{" "}
                  {estado.negociacao.statusDescricao ?? "Pendente"}
                </Typography>
                {estado.negociacao.itens.map((item, index) => (
                  <Box key={`${item.item}-${index}`}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{item.titulo}</Typography>
                      <Typography variant="body2">{valor(item.saldo)}</Typography>
                    </Stack>
                  </Box>
                ))}
              </Card>
            }
            resumo={
              <Card sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1">Valor do boleto</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vencimento em {dataCurta(estado.negociacao.vencimento)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color={theme.palette.secondary.main} fontWeight={700}>
                    {valor(estado.negociacao.valor)}
                  </Typography>
                </Stack>

                {!hasBoleto(estado.negociacao) && !hasPix(estado.negociacao) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Boleto em processamento.
                    <br />
                    Consulte em instantes.
                  </Typography>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={!hasBoleto(estado.negociacao) && !hasPix(estado.negociacao)}
                  onClick={() => setNegociacaoModal(estado.negociacao)}
                >
                  Visualizar boleto
                </Button>
              </Card>
            }
          />
        )}
      </Container>

      {negociacaoModal && (
        <BoletoUnicoModal open onClose={fecharModal} negociacao={negociacaoModal} />
      )}
    </Page>
  );
}

// ----------------------------------------------------------------------

function Grade({ principal, resumo }: { principal: React.ReactNode; resumo: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        alignItems: "start",
        gridTemplateColumns: { xs: "1fr", md: "1fr 360px" },
      }}
    >
      <Box>{principal}</Box>
      <Box sx={{ position: { md: "sticky" }, top: { md: 24 } }}>{resumo}</Box>
    </Box>
  );
}

function LinhaParcela({ item }: { item: ItemSimulacaoBoletoUnico }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Box>
        <Typography variant="body2" fontWeight={700}>
          {item.vencimentoOriginal ? mesAno(item.vencimentoOriginal) : `Parcela ${item.parcela}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vencimento em {dataCurta(item.vencimentoOriginal)}
        </Typography>
      </Box>
      <Typography variant="body2">{valor(item.saldoNegociado)}</Typography>
    </Stack>
  );
}

